import { forwardRef, useEffect, useRef, useState, type InputHTMLAttributes } from 'react';
import { Input, type ControlSize } from './input';

const formatter = new Intl.NumberFormat('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

export function parseMoney(value: string): number {
  const normalized = value.replace(/\s/g, '').replace(/\./g, '').replace(',', '.').replace(/[^\d.-]/g, '');
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
}

export interface InputMoneyProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'value' | 'onChange' | 'type'> {
  value: number;
  onChange: (value: number) => void;
  size?: ControlSize;
}

/** Currency input that keeps the form value numeric while displaying es-AR notation. */
export const InputMoney = forwardRef<HTMLInputElement, InputMoneyProps>(function InputMoney(
  { onChange, onBlur, value, ...props },
  ref,
) {
  const [display, setDisplay] = useState(value ? formatter.format(value) : '');
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (document.activeElement !== inputRef.current) setDisplay(value ? formatter.format(value) : '');
  }, [value]);

  return (
    <Input
      {...props}
      ref={(node) => {
        inputRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
      }}
      type="text"
      inputMode="decimal"
      value={display}
      onChange={(event) => {
        const next = event.target.value.replace(/[^\d,.-]/g, '');
        setDisplay(next);
        onChange(parseMoney(next));
      }}
      onBlur={(event) => {
        const next = parseMoney(event.target.value);
        setDisplay(next ? formatter.format(next) : '');
        onChange(next);
        onBlur?.(event);
      }}
    />
  );
});

InputMoney.displayName = 'InputMoney';
